import { createUuid } from '@notifi/uuid/browser';
import type { ChecklistLinkDraft, JobTypeFormValues } from './JobTypeFormDrawer';
import { toTermsPayload } from '@/lib/terms';
import type {
  ChecklistJobTypeLink,
  ChecklistTemplate,
  CreateJobTypeDto,
  JobType,
  JobTypeGateConfig,
  JobTypeSchedulingDefaults,
  RecurrenceFrequency,
  RequiredSkillDraft,
} from '@/types/settings';
import {
  type EditableLineItem,
  computeLinePreview,
} from '@/routes/_dashboard/dashboard/estimates/_shared';
import { buildJobLineItems } from '@/lib/jobs/jobFormDto';
import type { FormAudience, FormAutoAttach, FormSurface } from '@/types/formsEngine';
import { DEFAULT_JOB_TYPE_ICON_NAME } from '@/lib/design/job-type-appearance';

export type JobTypeChecklistDefinition = Pick<
  ChecklistTemplate,
  'id' | 'name' | 'description' | 'version' | 'status' | 'isDeleted'
> & {
  surface?: FormSurface;
  /** The template's "Completed by" choice — what actually gates the public form route. */
  publicFillable?: boolean;
  /**
   * Whether it is required to complete — the default a new link starts from.
   * A template stores no such flag, so this is how its links are already set,
   * or the choice made when it was just created for this job type.
   */
  blocksCompletion?: boolean;
  jobTypeLinks?: Array<
    ChecklistJobTypeLink & {
      audience?: FormAudience;
      instantiationOverride?: 'ON_DEMAND' | 'AUTO_ON_ATTACH' | null;
      autoAttach?: FormAutoAttach;
    }
  >;
};

interface JobTypeFormSerializationOptions {
  preserveUnspecifiedBusinessUnit?: boolean;
  persistedAuthorizationConfig?: JobTypeGateConfig | null;
  persistedSignoffConfig?: JobTypeGateConfig | null;
  /**
   * SPEC §4.1 (binary visibility) — update and duplicate paths. While Iris is
   * deactivated the Iris-only content is inert, so the dto OMITS
   * `customerPhrases`, `irisProfile`, `irisSurveyQuestions`, and
   * `irisAskEquipment` entirely. On an UPDATE the server writes those fields only when they are
   * present, which leaves the stored row byte-for-byte untouched. On a
   * DUPLICATE the new row simply starts with an empty Iris config (there is
   * nothing to "preserve" on a not-yet-created row, and silently normalizing
   * a deactivated source's stale content into a new row would be worse).
   * Carrying the content through instead would re-normalize it
   * (trim/dedupe/drop malformed FAQ lines) and legacy over-length content
   * would fail the server DTO's own validation — turning the blocked Save
   * this option exists to fix into a 400 on save/duplicate. The plain create
   * flow leaves this unset.
   */
  preserveInertIrisContent?: boolean;
  /**
   * Decision C tolerance (updates only). The backend treats a PRESENT
   * diagnostic/scheduling field as a touched field and re-validates the
   * one-off rule against the effective state — so serializing the unchanged
   * legacy `DIAGNOSTIC + RECURRING` combination on an unrelated edit
   * (rename, archive, visibility) would 400 the save and strand the row.
   * When set, each group is serialized only if the user actually touched it:
   * `serviceCall` gates scopeMode/serviceCallFeePolicy, `scheduling`
   * gates recurrenceFrequency/recurrenceInterval/schedulingDefaults. Create
   * flows leave this unset (a new row always writes everything).
   */
  updateDirtyGroups?: { serviceCall: boolean; scheduling: boolean };
}

function serializeGateConfig(
  required: boolean,
  persistedConfig?: JobTypeGateConfig | null,
): JobTypeGateConfig {
  if (!required) return { required: false };
  if (persistedConfig?.required) return persistedConfig;
  return { required: true, method: 'SIGNATURE' };
}

/**
 * Derive a 2-4 char uppercase short code from a job-type name, mirroring the
 * backend generator (operations JobTypesService). "HVAC Repair" -> "HVAC".
 * Falls back to a "JT"-padded value when the name has fewer than two
 * alphanumeric characters.
 */
export function deriveShortCodeFromName(name: string): string {
  const base = name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '')
    .slice(0, 4);
  return base.length >= 2 ? base : `${base}JT`.slice(0, 2);
}

/**
 * SPEC §4.1 (UI, 2026-08-03, binary visibility; fallback retired 2026-08-03) —
 * read the stored `irisMode` into the form's single Iris field: the activation
 * switch (activated: Iris knows the service exists; deactivated: Iris has no
 * idea it exists). Reading an older row without a stored mode maps to the
 * legacy server default (LISTED → activated) so an unrelated edit never
 * silently hides an exposed type.
 *
 * The diagnostic-fallback concept left the UI: a legacy irisMode='SERVICE_CALL'
 * row (or the older isServiceCallFallback marker, including the mixed
 * NOT_LISTED+marker shape — server-side those rows were always known to Iris)
 * reads back as plain activated=true, and saving it serializes LISTED with the
 * marker cleared. That is the intentional migration-by-touch: SERVICE_CALL
 * matches like LISTED everywhere (legacy compat read) and nothing new ever
 * writes it, so the first edit normalizes the row to the canonical shape.
 */
export function readIrisListingFields(
  jobType: Pick<Partial<JobType>, 'irisMode' | 'isServiceCallFallback'>,
): { irisActivated: boolean } {
  const storedMode =
    jobType.irisMode ?? (jobType.isServiceCallFallback ? 'SERVICE_CALL' : 'LISTED');
  return {
    irisActivated:
      storedMode === 'LISTED' ||
      storedMode === 'SERVICE_CALL' ||
      jobType.isServiceCallFallback === true,
  };
}

/** The visit-price rule options the drawer's pricing radio can write. */
export type ServiceCallFeePolicyField = 'CREDIT_ON_ESTIMATE' | 'WAIVE_ON_CALL' | 'NONE';

/**
 * Read a stored per-type fee policy into the drawer's visit-price radio.
 * Anything that is not one of the radio's own options (absent, null, or an
 * unknown stored value) reads back as the default job-credit option.
 */
export function readServiceCallFeePolicyField(
  stored: string | null | undefined,
): ServiceCallFeePolicyField {
  if (stored === 'NONE') return 'NONE';
  if (stored === 'CREDIT_ON_ESTIMATE') return 'CREDIT_ON_ESTIMATE';
  return 'WAIVE_ON_CALL';
}

// Decision C — serviceCallEnabled forces one-off scheduling. The exact
// message the backend 400s with; the client pre-blocks with the same words.
export const SERVICE_CALL_ONE_OFF_MESSAGE =
  'A diagnostic job type must be one-off — set its scheduling to one-off first';

// At least one required skill is mandatory — the scheduling engine is
// skill-typed, so a skill-less type can never be offered time slots. The one
// wording used by the blocked Save: toast, section copy, and inline error.
export const REQUIRED_SKILL_MESSAGE =
  'Add at least one required skill, or let any active technician take this job type — ' +
  'scheduling needs one or the other to find technicians.';

/**
 * The tenant-level "General service" skill is a backend mechanism, not a skill
 * anyone picks: it backs job types that require no specific skill (the
 * scheduling engine is skill-typed end to end, so a link has to exist), and
 * every active technician is granted it. The backend keeps it in sync with the
 * real skills — the moment a real skill is present it DELETES the general link.
 *
 * So offering it in the skills picker is a promise the save cannot keep: pick
 * it alongside a real skill and it silently disappears. The form models it as
 * the "any active technician" mode instead, and every read surface hides it.
 */
export const GENERAL_SERVICE_SKILL_SLUG = 'general-service';

export function isGeneralServiceSkill(skill: { slug?: string | null }): boolean {
  return skill.slug === GENERAL_SERVICE_SKILL_SLUG;
}

/** A job type's user-chosen skills — the auto-managed general one excluded. */
export function specificRequiredSkills<T extends { slug?: string | null }>(skills: T[]): T[] {
  return skills.filter((skill) => !isGeneralServiceSkill(skill));
}

/**
 * On-duty emergency scheduling needs a required skill in the payload, and the
 * server checks that BEFORE it would auto-link the general one — so "any active
 * technician" cannot carry an on-duty type in a tenant whose general skill row
 * does not exist yet (a failed provisioning seed; every other tenant has one
 * from the backfill). The server's own words, pre-blocked by the client the way
 * SERVICE_CALL_ONE_OFF_MESSAGE is.
 */
export const ON_DUTY_REQUIRES_SKILL_MESSAGE =
  'On-duty emergency scheduling requires at least one required skill.';

/**
 * The skills to WRITE for a job type. Never the general skill next to real ones
 * — the backend deletes that link — but keep it when it stands alone: it is how
 * "any active technician" is stored, and the guards that demand a required
 * skill (service-call fallback, on-duty scheduling) read the list we send.
 */
export function requiredSkillsForWrite<T extends { slug?: string | null }>(skills: T[]): T[] {
  const specific = specificRequiredSkills(skills);
  return specific.length > 0 ? specific : skills;
}

// Iris surveys (Decision D) — per-job-type intake questions Iris asks and
// saves onto the created job. Free-text answers, capped server-side at 10.
export const MAX_IRIS_SURVEY_QUESTIONS = 10;
export const MAX_IRIS_SURVEY_QUESTION_LENGTH = 200;

export interface IrisSurveyQuestionDraft {
  /** Stable id (kept across edits/reorders) answers are keyed by. */
  id: string;
  question: string;
  /** TEXT (default) free-text answer; PHOTO asks the customer to text a
   * picture, saved onto the job as an attachment. */
  type: 'TEXT' | 'PHOTO';
}

/** Stable id for a new survey question row (answers key off it forever). */
export function newIrisSurveyQuestionId(): string {
  return createUuid();
}

/** Hydrate stored survey questions (loose Json read) into editor rows. */
export function readIrisSurveyQuestions(value: unknown): IrisSurveyQuestionDraft[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const { id, question, type } = entry as { id?: unknown; question?: unknown; type?: unknown };
    if (typeof id !== 'string' || !id || typeof question !== 'string') return [];
    return [{ id, question, type: type === 'PHOTO' ? ('PHOTO' as const) : ('TEXT' as const) }];
  });
}

/**
 * Mirror the operations normalizer: trim + collapse whitespace, drop empty
 * rows, dedupe ids first-wins, cap at 10.
 */
export function normalizeIrisSurveyQuestions(
  questions: IrisSurveyQuestionDraft[],
): IrisSurveyQuestionDraft[] {
  const seen = new Set<string>();
  const normalized: IrisSurveyQuestionDraft[] = [];
  for (const entry of questions) {
    const question = entry.question.trim().replace(/\s+/g, ' ');
    if (!question || seen.has(entry.id)) continue;
    seen.add(entry.id);
    normalized.push({ id: entry.id, question, type: entry.type === 'PHOTO' ? 'PHOTO' : 'TEXT' });
    if (normalized.length >= MAX_IRIS_SURVEY_QUESTIONS) break;
  }
  return normalized;
}

const RECURRENCE_FREQUENCIES = new Set<RecurrenceFrequency>([
  'NONE',
  'DAY',
  'WEEK',
  'MONTH',
  'YEAR',
]);

export function normalizeJobTypeRecurrenceFrequency(
  value: unknown,
  supportsRecurring: boolean,
): RecurrenceFrequency {
  const normalized = RECURRENCE_FREQUENCIES.has(value as RecurrenceFrequency)
    ? (value as RecurrenceFrequency)
    : supportsRecurring
      ? 'WEEK'
      : 'NONE';

  return supportsRecurring && normalized === 'NONE' ? 'WEEK' : normalized;
}

export function resolveJobTypeRecurrenceCadence(input: {
  supportsRecurring: boolean;
  recurringDefaults?: Pick<JobTypeSchedulingDefaults['recurring'], 'frequency' | 'interval'> | null;
  legacyFrequency: unknown;
  legacyInterval: number;
}): { frequency: RecurrenceFrequency; interval: number } {
  const recurringDefaults = input.supportsRecurring ? input.recurringDefaults : null;

  return {
    frequency: normalizeJobTypeRecurrenceFrequency(
      recurringDefaults?.frequency ?? input.legacyFrequency,
      input.supportsRecurring,
    ),
    interval: recurringDefaults?.interval ?? input.legacyInterval,
  };
}

export function supportsRouteBasedRecurringPlacement(input: {
  supportsRecurring: boolean;
  patternMode: JobTypeFormValues['recurringPatternMode'];
  frequency: RecurrenceFrequency;
}): boolean {
  if (!input.supportsRecurring) return false;
  if (input.patternMode === 'FLEXIBLE') return true;
  return input.patternMode === 'FIXED' && input.frequency === 'WEEK';
}

function minimumDaysInConsecutiveMonths(monthCount: number): number {
  const normalizedMonths = Math.max(1, Math.trunc(monthCount));
  const commonYearMonthLengths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const fullYears = Math.floor(normalizedMonths / 12);
  const remainingMonths = normalizedMonths % 12;
  if (remainingMonths === 0) return fullYears * 365;

  let minimumRemainder = Number.POSITIVE_INFINITY;
  for (let startMonth = 0; startMonth < 12; startMonth += 1) {
    let days = 0;
    for (let offset = 0; offset < remainingMonths; offset += 1) {
      days += commonYearMonthLengths[(startMonth + offset) % 12]!;
    }
    minimumRemainder = Math.min(minimumRemainder, days);
  }
  return fullYears * 365 + minimumRemainder;
}

function guaranteedEligibleWeekdays(totalDays: number, eligibleDays: number): number {
  return Math.floor(totalDays / 7) * eligibleDays + Math.max(0, eligibleDays + (totalDays % 7) - 7);
}

export function recurringEligibleDaysPerCycle(input: {
  frequency: RecurrenceFrequency;
  interval: number;
  dayConstraintMode: JobTypeFormValues['recurringDayConstraintMode'];
  allowedWeekDays: number[];
}): number | null {
  const configuredDays = new Set(input.allowedWeekDays).size;
  const eligibleDaysPerWeek =
    input.dayConstraintMode === 'INCLUDE'
      ? configuredDays
      : input.dayConstraintMode === 'EXCLUDE'
        ? 7 - configuredDays
        : 7;
  const interval = Math.max(1, Math.trunc(input.interval));
  if (input.frequency === 'DAY') return 1;
  if (input.frequency === 'WEEK') return eligibleDaysPerWeek * interval;
  if (input.frequency === 'MONTH') {
    return guaranteedEligibleWeekdays(
      minimumDaysInConsecutiveMonths(interval),
      eligibleDaysPerWeek,
    );
  }
  if (input.frequency === 'YEAR') {
    return guaranteedEligibleWeekdays(365 * interval, eligibleDaysPerWeek);
  }
  return null;
}

export function checklistLinksForJobType(
  jobTypeId: string,
  definitions: JobTypeChecklistDefinition[],
): ChecklistLinkDraft[] {
  return definitions
    .flatMap((definition) => {
      const persistedLink = definition.jobTypeLinks?.find((link) => link.jobTypeId === jobTypeId);
      if (!persistedLink) return [];
      return [
        {
          checklistDefinitionId: definition.id,
          blocksCompletion: persistedLink.blocksCompletion ?? false,
          sortOrder: persistedLink.sortOrder ?? 0,
          audience: persistedLink.audience,
          instantiationOverride: persistedLink.instantiationOverride,
          autoAttach: persistedLink.autoAttach ?? null,
        },
      ];
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * The versioned schedulingDefaults write shape (v3). Extracted so the update
 * path can omit the whole scheduling group when untouched (Decision C
 * tolerance) without duplicating the serialization.
 */
function buildSchedulingDefaults(
  values: JobTypeFormValues,
  flags: {
    oneOffUsesPreset: boolean;
    oneOffSchedulesNow: boolean;
    recurringUsesPreset: boolean;
    recurringUsesFixedPattern: boolean;
    recurringSchedulesNow: boolean;
  },
): NonNullable<CreateJobTypeDto['schedulingDefaults']> {
  const {
    oneOffUsesPreset,
    oneOffSchedulesNow,
    recurringUsesPreset,
    recurringUsesFixedPattern,
    recurringSchedulesNow,
  } = flags;
  return {
    version: 3,
    availability: values.schedulingAvailability,
    oneOff: {
      patternMode: values.oneOffPatternMode,
      scheduleMode: oneOffUsesPreset ? values.oneOffScheduleMode : 'SCHEDULE_NOW',
      visitShape: oneOffSchedulesNow ? values.oneOffVisitShape : 'TIMED',
      preferredTimeWindow:
        oneOffSchedulesNow && values.oneOffVisitShape === 'TIMED'
          ? values.oneOffPreferredTimeWindow
          : 'ANY',
      specificStartTime:
        oneOffSchedulesNow &&
        values.oneOffVisitShape === 'TIMED' &&
        values.oneOffPreferredTimeWindow === 'SPECIFIC'
          ? values.oneOffSpecificStartTime
          : null,
      consecutiveDays:
        oneOffSchedulesNow && values.oneOffVisitShape === 'MULTI_DAY'
          ? values.oneOffConsecutiveDays
          : null,
      urgency: oneOffSchedulesNow ? values.oneOffUrgency : 'STANDARD',
    },
    recurring: {
      patternMode: values.recurringPatternMode,
      visitsPerCycle:
        recurringUsesPreset && values.recurringPatternMode === 'FLEXIBLE'
          ? values.recurringVisitsPerCycle
          : null,
      dayConstraintMode:
        recurringUsesPreset && values.recurringPatternMode === 'FLEXIBLE'
          ? values.recurringDayConstraintMode
          : 'ANY',
      allowedWeekDays:
        recurringUsesPreset && values.recurringPatternMode === 'FLEXIBLE'
          ? values.recurringAllowedWeekDays
          : [],
      placement: recurringUsesPreset ? values.recurringPlacement : 'SCHEDULE_NOW',
      frequency:
        values.schedulingAvailability === 'RECURRING' ? values.recurrenceFrequency : 'NONE',
      interval: values.schedulingAvailability === 'RECURRING' ? values.recurrenceInterval : 1,
      weekDays:
        recurringUsesFixedPattern && values.recurrenceFrequency === 'WEEK'
          ? values.recurringWeekDays
          : [],
      monthMode:
        recurringUsesFixedPattern && values.recurrenceFrequency === 'MONTH'
          ? values.recurringMonthMode
          : undefined,
      monthDay:
        recurringUsesFixedPattern &&
        values.recurrenceFrequency === 'MONTH' &&
        values.recurringMonthMode === 'DAY_OF_MONTH' &&
        !values.recurringMonthLastDay
          ? values.recurringMonthDay
          : null,
      monthLastDay:
        recurringUsesFixedPattern &&
        values.recurrenceFrequency === 'MONTH' &&
        values.recurringMonthMode === 'DAY_OF_MONTH'
          ? values.recurringMonthLastDay
          : false,
      monthDayOfWeek:
        recurringUsesFixedPattern &&
        values.recurrenceFrequency === 'MONTH' &&
        values.recurringMonthMode === 'DAY_OF_WEEK'
          ? values.recurringMonthDayOfWeek
          : null,
      monthDayOfWeekOccurrence:
        recurringUsesFixedPattern &&
        values.recurrenceFrequency === 'MONTH' &&
        values.recurringMonthMode === 'DAY_OF_WEEK'
          ? values.recurringMonthDayOfWeekOccurrence
          : null,
      annualMonth:
        recurringUsesFixedPattern && values.recurrenceFrequency === 'YEAR'
          ? values.recurringAnnualMonth
          : null,
      annualDay:
        recurringUsesFixedPattern && values.recurrenceFrequency === 'YEAR'
          ? values.recurringAnnualDay
          : null,
      timeMode: recurringSchedulesNow ? values.recurringTimeMode : 'PER_VISIT',
      technicianMode:
        recurringUsesPreset && values.recurringPlacement !== 'ROUTE_BASED'
          ? values.recurringTechnicianMode
          : 'PER_VISIT',
      visitStartTime:
        recurringSchedulesNow && values.recurringTimeMode === 'SAME'
          ? values.recurringVisitStartTime
          : null,
      visitEndTime:
        recurringSchedulesNow && values.recurringTimeMode === 'SAME'
          ? values.recurringVisitEndTime
          : null,
    },
  };
}

export function jobTypeFormValuesToDto(
  values: JobTypeFormValues,
  requiredSkills: RequiredSkillDraft[],
  lineItems: EditableLineItem[] = [],
  options: JobTypeFormSerializationOptions = {},
): CreateJobTypeDto {
  const oneOffUsesPreset =
    values.schedulingAvailability === 'ONE_OFF' && values.oneOffPatternMode === 'PRESET';
  const oneOffSchedulesNow = oneOffUsesPreset && values.oneOffScheduleMode === 'SCHEDULE_NOW';
  const recurringUsesPreset =
    values.schedulingAvailability === 'RECURRING' &&
    values.recurringPatternMode !== 'CHOOSE_ON_CREATE';
  const recurringUsesFixedPattern = recurringUsesPreset && values.recurringPatternMode === 'FIXED';
  const recurringSchedulesNow = recurringUsesPreset && values.recurringPlacement === 'SCHEDULE_NOW';
  const irisLines = (value: string) => {
    const seen = new Set<string>();
    return value
      .split(/\n+/)
      .map((entry) => entry.trim().replace(/\s+/g, ' '))
      .filter((entry) => {
        if (!entry) return false;
        const key = entry.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  return {
    name: values.name,
    shortCode: values.shortCode,
    color: values.color,
    // The picker always renders a concrete selection (the wrench when the user
    // never opened it), so the record has to store that same name. Persisting
    // `undefined` for the default made settings show an icon the row did not
    // actually carry, which dispatch chips — they render nothing without a
    // stored icon — then contradicted.
    icon: values.icon || DEFAULT_JOB_TYPE_ICON_NAME,
    pipelineId: values.pipelineId ?? null,
    // Persist the Business Unit set in preference order ([] unassigns).
    ...(options.preserveUnspecifiedBusinessUnit && values.businessUnitIds === undefined
      ? {}
      : { businessUnitIds: values.businessUnitIds ?? [] }),
    // Job tags stamped onto every created job ([] clears; an absent form
    // field leaves the stored set untouched).
    ...(values.tagIds !== undefined ? { tagIds: values.tagIds } : {}),
    description: values.description || undefined,
    // SPEC §4.1 (UI, 2026-08-03; fallback retired) — the activation switch is
    // the whole mapping: LISTED (Iris knows the service) or NOT_LISTED (Iris
    // has no idea it exists). Nothing writes SERVICE_CALL anymore — saving a
    // legacy SERVICE_CALL row (read back as activated) writes LISTED, the
    // intentional migration-by-touch. Deactivating never clears the Iris
    // config below — the values round-trip so reactivation restores them.
    irisMode: values.irisActivated ? 'LISTED' : 'NOT_LISTED',
    // Always false: the fallback designation is retired. Sending the explicit
    // false clears the legacy marker on touch — otherwise a mixed legacy row
    // (NOT_LISTED + marker) would keep reading back as activated after the
    // user deactivates it.
    isServiceCallFallback: false,
    // Diagnostic scope is configured with Iris because it changes how Iris
    // explains and sells the visit. On update the group is serialized
    // only when touched (see updateDirtyGroups) — presence IS touch to the
    // backend's one-off rule.
    ...((options.updateDirtyGroups?.serviceCall ?? true)
      ? {
          scopeMode: values.isDiagnostic ? 'DIAGNOSTIC' : 'DEFINED',
          // Visit-price rule (the pricing-section radio, the ONLY fee-policy
          // surface). Written only while the capability is on — with it off the
          // key is omitted so the stored value stays untouched.
          ...(values.isDiagnostic ? { serviceCallFeePolicy: values.serviceCallFeePolicy } : {}),
        }
      : {}),
    // Visit deposit — available on every job type (not a service-call-only
    // option). The flag always writes; type/value only while it is on so a
    // stored config survives a toggle-off round trip.
    visitDepositEnabled: values.visitDepositEnabled,
    ...(values.visitDepositEnabled
      ? {
          visitDepositType: values.visitDepositType,
          visitDepositValue: values.visitDepositValue,
        }
      : {}),
    // Iris-only content. On an update with Iris deactivated the keys are
    // omitted (see preserveInertIrisContent): the server preserves the stored
    // values untouched, so a legacy row with stale/invalid Iris content can
    // Save while deactivated and later reactivate with its content intact.
    ...(options.preserveInertIrisContent && !values.irisActivated
      ? {}
      : {
          // Decisions D+E — survey questions + equipment awareness live with
          // the rest of the Iris config: inert-and-preserved while deactivated.
          irisSurveyQuestions: normalizeIrisSurveyQuestions(values.irisSurveyQuestions),
          irisSurveyTiming: values.irisSurveyTiming,
          irisAskEquipment: values.irisAskEquipment,
          customerPhrases: (() => {
            const seen = new Set<string>();
            return values.customerPhrases
              .split(/[\n,]+/)
              .map((phrase) => phrase.trim().replace(/\s+/g, ' '))
              .filter((phrase) => {
                if (!phrase) return false;
                const key = phrase.toLowerCase();
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });
          })(),
          irisProfile: {
            version: 1 as const,
            customerDescription: values.irisCustomerDescription.trim() || undefined,
            matchingExclusions: irisLines(values.irisMatchingExclusions),
            includedWork: irisLines(values.irisIncludedWork),
            excludedWork: irisLines(values.irisExcludedWork),
            faqs: irisLines(values.irisFaqs).flatMap((entry) => {
              const separator = entry.indexOf('|');
              if (separator < 0) return [];
              const question = entry.slice(0, separator).trim();
              const answer = entry.slice(separator + 1).trim();
              return question && answer ? [{ question, answer }] : [];
            }),
            warranty: values.irisWarranty.trim() || undefined,
            additionalGuidance: values.irisAdditionalGuidance.trim() || undefined,
          },
        }),
    onDutySchedulingEnabled: values.onDutySchedulingEnabled,
    requiresParts: values.requiresParts,
    defaultDuration: values.defaultDuration,
    defaultPriority: values.defaultPriority,
    // Scheduling configuration — same touched-only serialization on update as
    // the service-call group above.
    ...((options.updateDirtyGroups?.scheduling ?? true)
      ? {
          recurrenceFrequency:
            values.schedulingAvailability === 'ONE_OFF' ? 'NONE' : values.recurrenceFrequency,
          recurrenceInterval:
            values.schedulingAvailability === 'RECURRING' ? values.recurrenceInterval : 1,
          schedulingDefaults: buildSchedulingDefaults(values, {
            oneOffUsesPreset,
            oneOffSchedulesNow,
            recurringUsesPreset,
            recurringUsesFixedPattern,
            recurringSchedulesNow,
          }),
        }
      : {}),
    minCrewSize: values.minCrewSize,
    // The job type's price is now defined by pricebook line items. Send the
    // full editor state (an empty array clears them) so the form is
    // authoritative; the free-form defaultPrice is no longer edited here.
    // Persist the editor's computed per-line totals so downstream consumers with
    // no pricing engine (online-booking price fallback) read T&M / cost-plus
    // lines correctly instead of basePrice * quantity. Tax is resolved per
    // booking, so the snapshot total is pre-tax (taxResolution = null).
    lineItems:
      buildJobLineItems(lineItems, 'STANDARD', {
        computePreview: (row, mode) => {
          const { lineSubtotal, lineTotal } = computeLinePreview(row, mode, null);
          return { lineSubtotal, lineTotal };
        },
      }) ?? [],
    requiredSkills: requiredSkills.map((skill) => ({
      serviceTypeId: skill.serviceTypeId,
    })),
    authorizationConfig: serializeGateConfig(
      values.authRequired,
      options.persistedAuthorizationConfig,
    ),
    signoffConfig: serializeGateConfig(values.signoffRequired, options.persistedSignoffConfig),
    cancellationWindowHours:
      typeof values.cancellationWindowHours === 'number' ? values.cancellationWindowHours : null,
    cancellationFeeType: values.cancellationFeeType,
    // A NONE policy ignores the amount; clear it so a stale value isn't persisted.
    cancellationFeeAmount:
      values.cancellationFeeType !== 'NONE' && typeof values.cancellationFeeAmount === 'number'
        ? values.cancellationFeeAmount
        : null,
    depositRefundable: values.depositRefundable,
    customerRescheduleMode: values.customerRescheduleMode,
    ...(values.customerRescheduleMode !== 'DISABLED'
      ? { rescheduleCutoffHours: values.rescheduleCutoffHours }
      : {}),
    ...(values.customerRescheduleMode === 'SELF_SERVICE'
      ? { maxCustomerReschedules: values.maxCustomerReschedules }
      : {}),
    // NOT-362 — inheritable workflow defaults.
    defaultStageId: values.defaultStageId ?? null,
    schedulingNotificationPolicy: values.schedulingNotificationPolicy ?? 'ASK',
    confirmationMethod: values.confirmationMethod,
    invoicingMethod: values.invoicingMethod,
    invoicingSchedule: values.invoicingSchedule,
    autoInvoice: values.autoInvoice,
    autoCharge: values.autoCharge,
    reviewRequestDefault: values.reviewRequestDefault,
    weatherPolicy: values.weatherPolicy,
    // T&C default cascaded onto jobs of this type. Always sent so switching
    // back to "default" is expressible (an omitted key is a server-side no-op).
    ...toTermsPayload({
      termsMode: values.termsMode,
      termsTemplateIds: values.termsTemplateIds,
      termsAndConditions: values.termsAndConditions,
    }),
  };
}
